import { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ShieldAlert, Search, Users, Sword, Crown, AlertTriangle, LifeBuoy, X, Receipt, ChevronLeft, ChevronRight, Image as ImageIcon, Bell, Pickaxe, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Empty } from "@/components/Page";
import { inputCls } from "@/components/admin/shared";
import { FeedbackPanel } from "@/components/admin/FeedbackPanel";
import { FragEventPanel } from "@/components/admin/FragEventPanel";
import { SkillEventPanel } from "@/components/admin/SkillEventPanel";
import { ScoutEventPanel } from "@/components/admin/ScoutEventPanel";
import { SandsEaseEventPanel } from "@/components/admin/SandsEaseEventPanel";
import { OreRushEventPanel } from "@/components/admin/OreRushEventPanel";
import { CampaignSupportPanel } from "@/components/admin/CampaignSupportPanel";
import { HotSpringsPanel } from "@/components/admin/HotSpringsPanel";
import { TrainingMakeupPanel } from "@/components/admin/TrainingMakeupPanel";
import { RecoverRestedPanel } from "@/components/admin/RecoverRestedPanel";
import { TrainingAuditPanel } from "@/components/admin/TrainingAuditPanel";
import { BossEventPanel } from "@/components/admin/BossEventPanel";
import { PlayTestPanel } from "@/components/admin/PlayTestPanel";
import { CampaignRewardTestPanel } from "@/components/admin/CampaignRewardTestPanel";
import { WorkshopPanel } from "@/components/admin/WorkshopPanel";
import { AwardGoldPanel } from "@/components/admin/AwardGoldPanel";
import { EditFollisPanel } from "@/components/admin/EditFollisPanel";
import { MassEmailPanel } from "@/components/admin/MassEmailPanel";
import { VipBonusReconcilePanel } from "@/components/admin/VipBonusReconcilePanel";
import { RevengeWeekPanel } from "@/components/admin/RevengeWeekPanel";
import { BountyWeekPanel } from "@/components/admin/BountyWeekPanel";
import { ImperatorFavorPanel } from "@/components/admin/ImperatorFavorPanel";
import { WanderingVeteranPanel } from "@/components/admin/WanderingVeteranPanel";
import { BackupsPanel } from "@/components/admin/BackupsPanel";
import { PurgeRetiredPanel } from "@/components/admin/PurgeRetiredPanel";
import { RepairSlotsPanel } from "@/components/admin/RepairSlotsPanel";
import { PurchasesPanel } from "@/components/admin/PurchasesPanel";
import { VipUsersPanel } from "@/components/admin/VipUsersPanel";
import { SecurityPanel } from "@/components/admin/SecurityPanel";
import { ReferralsPanel } from "@/components/admin/ReferralsPanel";
import { ProfilePicturesPanel } from "@/components/admin/ProfilePicturesPanel";
import { AllianceWarsPanel } from "@/components/admin/AllianceWarsPanel";
import { HouseGamesPanel } from "@/components/admin/HouseGamesPanel";
import { DuelWagerPanel } from "@/components/admin/DuelWagerPanel";
import { ManagerModal } from "@/components/admin/ManagerModal";
import { GladiatorModal } from "@/components/admin/GladiatorModal";

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("managers");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [selManager, setSelManager] = useState(null);
  const [selGlad, setSelGlad] = useState(null);
  const [ipFilter, setIpFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [alertsCount, setAlertsCount] = useState(0);

  const loadStats = useCallback(() => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}), []);
  useEffect(() => { loadStats(); }, [loadStats]);

  // Load admin alerts count
  useEffect(() => {
    api.get("/admin/alerts", { params: { page: 1, limit: 1 } })
      .then((r) => setAlertsCount(r.data.unread_count))
      .catch(() => {});
  }, []);

  // Reset to first page whenever the tab or filters change.
  useEffect(() => { setPage(1); }, [tab, q, ipFilter]);

  useEffect(() => {
    setRows(null);
    const t = setTimeout(() => {
      if (tab !== "managers" && tab !== "gladiators") { setRows([]); return; }
      const url = tab === "managers" ? "/admin/managers" : "/admin/gladiators";
      const ipq = tab === "managers" && ipFilter ? `&ip=${encodeURIComponent(ipFilter)}` : "";
      const pageq = tab === "managers" ? `&page=${page}` : "";
      api.get(`${url}?q=${encodeURIComponent(q)}${ipq}${pageq}`).then((r) => {
        setRows(tab === "managers" ? r.data.managers : r.data.gladiators);
        if (tab === "managers") { setTotalPages(r.data.total_pages || 1); setTotal(r.data.total || 0); }
      }).catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [tab, q, ipFilter, page]);

  const refresh = () => {
    setRows(null);
    const url = tab === "managers" ? "/admin/managers" : "/admin/gladiators";
    const ipq = tab === "managers" && ipFilter ? `&ip=${encodeURIComponent(ipFilter)}` : "";
    const pageq = tab === "managers" ? `&page=${page}` : "";
    api.get(`${url}?q=${encodeURIComponent(q)}${ipq}${pageq}`).then((r) => {
      setRows(tab === "managers" ? r.data.managers : r.data.gladiators);
      if (tab === "managers") { setTotalPages(r.data.total_pages || 1); setTotal(r.data.total || 0); }
    });
    loadStats();
  };

  if (user && !user.is_admin) return <Navigate to="/" replace />;

  return (
    <div>
      <a href="https://lanistamanager.com/unique">Visitors</a>
      <PageHeader title="Admin Dashboard" subtitle="Search, inspect and edit every lanista and gladiator" icon={ShieldAlert} />

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[["Managers", stats.managers], ["Active Glads", stats.gladiators_active], ["Retired", stats.gladiators_retired], ["VIP", stats.vip], ["Online", stats.online]].map(([l, v]) => (
            <div key={l} className="border border-amber-900/30 bg-zinc-950 px-4 py-3">
              <div className="font-cinzel text-2xl text-amber-400 tabular-nums">{v}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{l}</div>
            </div>
          ))}
        </div>
      )}
      <HouseGamesPanel />
      <DuelWagerPanel />

      {/* Admin Alerts Quick Link */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={() => navigate("/admin/alerts")}
          className="flex items-center justify-between px-4 py-3 border border-amber-900/40 bg-amber-950/20 hover:bg-amber-950/30 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Bell className="text-amber-400" size={20} />
            <div className="text-left">
              <div className="font-cinzel text-amber-300 uppercase tracking-wide">Admin Alerts</div>
              <div className="text-xs text-zinc-400">System notifications and moderation alerts</div>
            </div>
          </div>
          {alertsCount > 0 && (
            <span className="min-w-[24px] h-[24px] px-2 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-sm font-bold tabular-nums">
              {alertsCount}
            </span>
          )}
        </button>

        <button
          onClick={() => navigate("/admin/promo-codes")}
          className="flex items-center justify-between px-4 py-3 border border-amber-900/40 bg-zinc-950/60 hover:bg-zinc-900/80 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Receipt className="text-amber-400" size={20} />
            <div className="text-left">
              <div className="font-cinzel text-amber-300 uppercase tracking-wide">Promo Codes</div>
              <div className="text-xs text-zinc-400">Create and manage promotional codes</div>
            </div>
          </div>
        </button>

        <button
          data-testid="admin-open-mining-controls"
          onClick={() => navigate("/admin/mining")}
          className="flex items-center justify-between px-4 py-3 border border-amber-900/40 bg-zinc-950/60 hover:bg-zinc-900/80 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Pickaxe className="text-amber-400" size={20} />
            <div className="text-left">
              <div className="font-cinzel text-amber-300 uppercase tracking-wide">Mining Controls</div>
              <div className="text-xs text-zinc-400">Mines, smelting, smithing costs, and pickaxe economy</div>
            </div>
          </div>
        </button>

        <button
          data-testid="admin-open-merchant-manager"
          onClick={() => navigate("/admin/merchant")}
          className="flex items-center justify-between px-4 py-3 border border-amber-900/40 bg-zinc-950/60 hover:bg-zinc-900/80 transition-colors"
        >
          <div className="flex items-center gap-3">
            <ShoppingBag className="text-amber-400" size={20} />
            <div className="text-left">
              <div className="font-cinzel text-amber-300 uppercase tracking-wide">Merchant Manager</div>
              <div className="text-xs text-zinc-400">Traveling Merchant inventory, pricing, and always-available shelf</div>
            </div>
          </div>
        </button>

        <button
          data-testid="admin-open-wiki-controls"
          onClick={() => navigate("/admin/wiki")}
          className="flex items-center justify-between px-4 py-3 border border-amber-900/40 bg-zinc-950/60 hover:bg-zinc-900/80 transition-colors"
        >
          <div className="text-left">
            <div className="font-cinzel text-amber-300 uppercase tracking-wide">Wiki Manager</div>
            <div className="text-xs text-zinc-400">Sections, pages, homepage content, and publishing</div>
          </div>
        </button>

        <button
          data-testid="admin-open-ludus-trias-controls"
          onClick={() => navigate("/admin/ludus-trias")}
          className="flex items-center justify-between px-4 py-3 border border-amber-900/40 bg-zinc-950/60 hover:bg-zinc-900/80 transition-colors"
        >
          <div className="text-left">
            <div className="font-cinzel text-amber-300 uppercase tracking-wide">Ludus Trias Rewards</div>
            <div className="text-xs text-zinc-400">Edit monthly season titles, currency, and item chests</div>
          </div>
        </button>

        <button
          data-testid="admin-open-wheel-fortuna-controls"
          onClick={() => navigate("/admin/wheel-fortuna")}
          className="flex items-center justify-between px-4 py-3 border border-amber-900/40 bg-zinc-950/60 hover:bg-zinc-900/80 transition-colors"
        >
          <div className="text-left">
            <div className="font-cinzel text-amber-300 uppercase tracking-wide">Wheel of Fortuna</div>
            <div className="text-xs text-zinc-400">Rewards, slice weights, and free-spin reset timing</div>
          </div>
        </button>

        <button
          onClick={() => navigate("/admin/tournaments")}
          className="flex items-center justify-between px-4 py-3 border border-red-900/40 bg-red-950/20 hover:bg-red-950/30 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Crown className="text-red-400" size={20} />
            <div className="text-left">
              <div className="font-cinzel text-red-300 uppercase tracking-wide">🚨 Tournament Emergency</div>
              <div className="text-xs text-red-300 font-bold">Manual controls & system diagnostics</div>
            </div>
          </div>
        </button>

        <button
          data-testid="admin-beast-registry-link"
          onClick={() => navigate("/admin/beasts")}
          className="flex items-center justify-between px-4 py-3 border border-emerald-900/40 bg-emerald-950/20 hover:bg-emerald-950/30 transition-colors"
        >
          <div className="text-left">
            <div className="font-cinzel text-emerald-300 uppercase tracking-wide">Beast Registry</div>
            <div className="text-xs text-zinc-400">Inspect every caught and bred beast</div>
          </div>
        </button>

        <button
          data-testid="admin-tournament-integrity-link"
          onClick={() => navigate("/admin/tournament-integrity")}
          className="flex items-center justify-between px-4 py-3 border border-rose-900/40 bg-rose-950/20 hover:bg-rose-950/30 transition-colors"
        >
          <div className="text-left">
            <div className="font-cinzel text-rose-300 uppercase tracking-wide">Tournament Integrity</div>
            <div className="text-xs text-zinc-400">Inspect active rounds and guarded recovery actions</div>
          </div>
        </button>
      </div>

      <SecurityPanel />
      <ReferralsPanel />
      <ProfilePicturesPanel />
      <FragEventPanel />
      <SkillEventPanel />
      <ScoutEventPanel />
      <SandsEaseEventPanel />
      <OreRushEventPanel />
      <HotSpringsPanel />
      <TrainingMakeupPanel />
      <RecoverRestedPanel />
      <TrainingAuditPanel />
      <BossEventPanel />
      <PlayTestPanel />
      <CampaignRewardTestPanel />
      <CampaignSupportPanel />
      <WorkshopPanel />
      <AwardGoldPanel />
      <EditFollisPanel />
      <MassEmailPanel />
      <VipBonusReconcilePanel />
      <RevengeWeekPanel />
      <BountyWeekPanel />
      <ImperatorFavorPanel />
      <WanderingVeteranPanel />
      <BackupsPanel />
      <PurgeRetiredPanel />
      <RepairSlotsPanel />
      <AllianceWarsPanel />

      <div className="flex gap-2 mb-4 border-b border-zinc-800">
        {[["managers", "Managers", Users], ["gladiators", "Gladiators", Sword], ["purchases", "Purchases", Receipt], ["vip", "VIP", Crown], ["reports", "Reports", LifeBuoy]].map(([id, label, Icon]) => (
          <button key={id} data-testid={`admin-tab-${id}`} onClick={() => { setTab(id); setQ(""); }}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide border-b-2 -mb-px flex items-center gap-2 transition-colors ${tab === id ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-amber-300"}`}>
            <Icon size={15} /> {label}
            {id === "reports" && stats?.feedback_open > 0 && (
              <span data-testid="admin-reports-badge" className="ml-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold tabular-nums">{stats.feedback_open}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "reports" ? (
        <FeedbackPanel onChanged={loadStats} />
      ) : tab === "purchases" ? (
        <PurchasesPanel />
      ) : tab === "vip" ? (
        <VipUsersPanel />
      ) : (
      <>
      <div className="relative mb-4 max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={tab === "managers" ? "Search by name or email…" : "Search gladiators by name…"}
          className={`${inputCls} pl-9`} />
      </div>

      {tab === "managers" && ipFilter && (
        <div data-testid="admin-ip-filter-chip" className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm bg-red-950/40 border border-red-800/50 text-red-200">
          <AlertTriangle size={13} /> Accounts on IP <span className="font-medium tabular-nums">{ipFilter}</span>
          <button data-testid="admin-ip-filter-clear" onClick={() => setIpFilter("")} className="ml-1 text-red-300 hover:text-white"><X size={14} /></button>
        </div>
      )}

      {rows === null ? <Loader /> : rows.length === 0 ? <Empty label="No results." /> : (
        <div className="border border-amber-900/30 overflow-x-auto">
          {tab === "managers" ? (
            <table className="w-full text-sm">
              <thead><tr className="bg-zinc-950 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-amber-900/30">
                <th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3 hidden sm:table-cell">Email</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Last Login</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">IP Address</th>
                <th className="text-left px-4 py-3">Role</th><th className="text-center px-4 py-3">VIP</th>
                <th className="text-right px-4 py-3">Glads</th>
                <th className="text-right px-4 py-3">Slots</th></tr></thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.user_id} data-testid={`admin-manager-row-${m.user_id}`} onClick={() => setSelManager(m.user_id)}
                    className="border-b border-zinc-900 hover:bg-zinc-900/50 cursor-pointer">
                    <td className="px-4 py-3 text-zinc-100">{m.name}{m.is_admin && <Crown size={12} className="inline ml-1.5 text-amber-500" />}</td>
                    <td className="px-4 py-3 text-zinc-400 hidden sm:table-cell">{m.email}</td>
                    <td className="px-4 py-3 text-zinc-400 hidden lg:table-cell tabular-nums" data-testid={`admin-last-login-${m.user_id}`}>
                      {m.last_login ? new Date(m.last_login).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : <span className="text-zinc-700">—</span>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell" data-testid={`admin-ip-${m.user_id}`}>
                      {m.last_ip ? (
                        m.shared_ip ? (
                          <button data-testid={`admin-ip-shared-${m.user_id}`} title={`${m.ip_account_count} accounts share this IP — click to view them`}
                            onClick={(e) => { e.stopPropagation(); setQ(""); setIpFilter(m.last_ip); }}
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[12px] font-medium text-red-300 bg-red-950/40 border border-red-800/50 hover:bg-red-900/50 hover:border-red-600 transition-colors tabular-nums">
                            <AlertTriangle size={12} /> {m.last_ip} <span className="text-red-400/80">×{m.ip_account_count}</span>
                          </button>
                        ) : (
                          <button onClick={(e) => { e.stopPropagation(); setQ(""); setIpFilter(m.last_ip); }}
                            className="text-zinc-500 hover:text-amber-400 tabular-nums transition-colors" title="Click to view accounts on this IP">{m.last_ip}</button>
                        )
                      ) : <span className="text-zinc-700">—</span>}
                    </td>
                    <td className="px-4 py-3"><span className={m.role === "admin" ? "text-amber-400" : "text-zinc-400"}>{m.role}</span></td>
                    <td className="px-4 py-3 text-center">{m.vip ? <Crown size={14} className="inline text-amber-400" /> : <span className="text-zinc-700">—</span>}</td>
                    <td className="px-4 py-3 text-right text-zinc-300 tabular-nums">{m.active_gladiators}<span className="text-zinc-600">/{m.active_gladiators + m.retired_gladiators}</span></td>
                    <td className="px-4 py-3 text-right tabular-nums" data-testid={`admin-slots-${m.user_id}`}
                      title={`Total gladiator slots (roster cap)${m.admin_bonus_slots ? ` — includes ${m.admin_bonus_slots} admin-awarded` : ""}`}>
                      <span className="text-sky-300 font-semibold">{m.roster_cap ?? "—"}</span>
                      {m.admin_bonus_slots > 0 && <span className="text-emerald-400/80 text-[11px] ml-1">(+{m.admin_bonus_slots})</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="bg-zinc-950 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-amber-900/30">
                <th className="text-left px-4 py-3">Gladiator</th><th className="text-left px-4 py-3 hidden sm:table-cell">Class</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Owner</th><th className="text-right px-4 py-3">Rating</th>
                <th className="text-center px-4 py-3">Status</th></tr></thead>
              <tbody>
                {rows.map((g) => (
                  <tr key={g.id} data-testid={`admin-glad-row-${g.id}`} onClick={() => setSelGlad(g.id)}
                    className="border-b border-zinc-900 hover:bg-zinc-900/50 cursor-pointer">
                    <td className="px-4 py-3 text-zinc-100">{g.name}</td>
                    <td className="px-4 py-3 text-zinc-400 hidden sm:table-cell">{g.cls}</td>
                    <td className="px-4 py-3 text-zinc-400 hidden md:table-cell">{g.owner_name}</td>
                    <td className="px-4 py-3 text-right text-amber-300 font-semibold tabular-nums">{g.rating}</td>
                    <td className="px-4 py-3 text-center text-xs">{g.retired ? <span className="text-zinc-500">Retired</span> : <span className="text-emerald-400">Active</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "managers" && rows && rows.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4" data-testid="admin-managers-pagination">
          <span className="text-[11px] uppercase tracking-widest text-zinc-500 tabular-nums">{total} lanistae</span>
          <div className="flex items-center gap-4">
            <button data-testid="admin-managers-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
              className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-amber-300 disabled:opacity-30 disabled:hover:text-zinc-400 transition-colors">
              <ChevronLeft size={13} /> Prev
            </button>
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 tabular-nums" data-testid="admin-managers-page-indicator">Page {page} of {totalPages}</span>
            <button data-testid="admin-managers-next" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-amber-300 disabled:opacity-30 disabled:hover:text-zinc-400 transition-colors">
              Next <ChevronRight size={13} />
            </button>
          </div>
        </div>
      )}
      </>
      )}

      {selManager && <ManagerModal userId={selManager} onClose={() => setSelManager(null)} onChanged={refresh} onOpenGlad={(gid) => { setSelManager(null); setSelGlad(gid); }} onFilterIp={(ip) => { setSelManager(null); setQ(""); setIpFilter(ip); }} />}
      {selGlad && <GladiatorModal gladId={selGlad} onClose={() => setSelGlad(null)} onChanged={refresh} />}
    </div>
  );
}
